'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkflowStore } from '../../../stores/workflowStore';

interface ExportConfigProps {
  nodeId: string;
  config: { 
    format?: 'excel' | 'csv';
    fileName?: string; 
    sheetName?: string;
    separator?: string;
    encoding?: string;
  };
}

export function ExportConfig({ nodeId, config }: ExportConfigProps) {
  const { t } = useTranslation();
  const { updateNodeConfig } = useWorkflowStore();
  
  // 默认值为 excel 格式
  const [format, setFormat] = useState<'excel' | 'csv'>(config.format || 'excel');
  const [fileName, setFileName] = useState(config.fileName || 'output');
  const [sheetName, setSheetName] = useState(config.sheetName || 'Sheet1');
  const [separator, setSeparator] = useState(config.separator || ',');
  const [encoding, setEncoding] = useState(config.encoding || 'utf-8');

  // 当 config 变化时（如 AI 生成工作流后），同步更新内部状态
  useEffect(() => {
    if (config.format) setFormat(config.format as 'excel' | 'csv');
    if (config.fileName) setFileName(config.fileName);
    if (config.sheetName) setSheetName(config.sheetName);
    if (config.separator) setSeparator(config.separator);
    if (config.encoding) setEncoding(config.encoding);
  }, [config.format, config.fileName, config.sheetName, config.separator, config.encoding]);

  const save = (updates: Partial<typeof config>) => {
    const newFormat = updates.format !== undefined ? updates.format : format;
    const newFileName = updates.fileName !== undefined ? updates.fileName : fileName;
    const newSheetName = updates.sheetName !== undefined ? updates.sheetName : sheetName;
    const newSeparator = updates.separator !== undefined ? updates.separator : separator;
    const newEncoding = updates.encoding !== undefined ? updates.encoding : encoding;
    
    updateNodeConfig(nodeId, { 
      type: 'export', 
      params: { 
        format: newFormat, 
        fileName: newFileName, 
        sheetName: newSheetName,
        separator: newSeparator,
        encoding: newEncoding
      } 
    });
  };

  return (
    <div className="space-y-4">
      {/* 格式选择 */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-500">{t('nodes.config.export.format')}</label>
        <select 
          value={format} 
          onChange={(e) => { 
            const newFormat = e.target.value as 'excel' | 'csv';
            setFormat(newFormat); 
            save({ format: newFormat }); 
          }}
          className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
        >
          <option value="excel">Excel (.xlsx)</option>
          <option value="csv">CSV (.csv)</option>
        </select>
      </div>

      {/* 文件名 */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-500">{t('nodes.config.export.filename')}</label>
        <input 
          type="text" 
          placeholder={t('nodes.config.export.filenamePlaceholder')} 
          value={fileName}
          onChange={(e) => { setFileName(e.target.value); save({ fileName: e.target.value }); }}
          className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 placeholder:text-zinc-400"
        />
        <p className="text-xs text-zinc-400">
          {format === 'excel' 
            ? t('nodes.config.export.finalExcelName', { name: fileName || 'output' })
            : t('nodes.config.export.finalCsvName', { name: fileName || 'output' })
          }
        </p>
      </div>

      {/* Excel 特有配置 */}
      {format === 'excel' && (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-zinc-500">{t('nodes.config.export.sheetName')}</label>
          <input 
            type="text" 
            placeholder={t('nodes.config.export.sheetNamePlaceholder')} 
            value={sheetName}
            onChange={(e) => { setSheetName(e.target.value); save({ sheetName: e.target.value }); }}
            className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 placeholder:text-zinc-400"
          />
        </div>
      )}

      {/* CSV 特有配置 */}
      {format === 'csv' && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-zinc-500">
              {t('nodes.config.export.separator')}
            </label>
            <select 
              value={separator} 
              onChange={(e) => { setSeparator(e.target.value); save({ separator: e.target.value }); }}
              className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-emerald-500/20 focus:ring-2 focus:border-emerald-500"
            >
              <option value=",">{t('nodes.config.export.comma')}</option>
              <option value="\t">{t('nodes.config.export.tab')}</option>
              <option value=";">{t('nodes.config.export.semicolon')}</option>
              <option value="|">{t('nodes.config.export.pipe')}</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-zinc-500">{t('nodes.config.export.encoding')}</label>
            <select 
              value={encoding} 
              onChange={(e) => { setEncoding(e.target.value); save({ encoding: e.target.value }); }}
              className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            >
              <option value="utf-8">{t('nodes.config.export.utf8')}</option>
              <option value="gbk">{t('nodes.config.export.gbk')}</option>
              <option value="gb2312">GB2312</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
